import { AfterViewInit, Component, ElementRef, HostListener, Inject, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FilterControlsFactory } from '../../../services/actions/models/filter-controls-factory';
import { DestroySubscribers } from '../../../decorators/destroy-subscribers.decorator';
import { PageSettings, TableInterface } from '../../table-layout/table-layout.model';
import { UntypedFormControl } from '@angular/forms';
import { StorageMap } from '@ngx-pwa/local-storage';
import { IColumn, IFilter, IRow, IScopeValues, ITableData, ITableDataResponse } from '../service-facade/data.response';
import { IUnsecuredService } from '../../../services/unsecured/unsecured-service.interface';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { IFilterModel } from '../../../services/actions/models/filter-model.interface';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  IVisibilityValues,
  IOrderValues,
  IFilterValues,
  ITABLE_SERVICE,
  ITableService,
} from '../../../services/tables/interfaces/table.service.interface';
import { ISortedColumns } from '../../../services/data/data-service.interface';
import { TableComponent } from './table.component';
import { DisplayFilterInterface, TIMEZONE_CONFIG } from '@platform500services/p500-ui-kit';
import { TimeZoneConfig } from '../../../time-zone/time-zone.config';
import { IPaginationParams, ITableParams } from './interfaces/table-params.interface';
import { clearObj } from '../../../helpers/clearObj';
import { getDisplayFilters } from '../header-facade/header-facade.helpers';
import { ITableSettings } from './interfaces/table-settings.interface';
import { TABLE_TRANSLATIONS } from './table-translation.const';

@Component({
  selector: 'gtd-table-facade-new',
  templateUrl: './table-facade.component.html',
})
@DestroySubscribers()
export class TableFacadeNewComponent implements OnChanges, AfterViewInit {
  @Input() title!: string;
  @Input() key!: string;
  @Input() settings!: PageSettings;

  @ViewChild(TableComponent) table?: TableComponent;
  @ViewChild('mainContainer') mainContainer!: ElementRef;

  public data!: ITableData | null;
  public filtersModel!: IFilterModel[];
  public quickFilters: IFilter[];
  public visibleColumns!: IColumn[];
  public columns: IColumn[] = [];
  public currentSettings: TableInterface | undefined;
  public displayFilters!: DisplayFilterInterface[];
  private routeParams!: Params;
  public tableParams!: ITableParams | null;
  public pagination: IPaginationParams;
  public isLoading = true;
  public dataNotFoundWithAction = $localize`:@@tables.data_not_found_with_action:Data wasn't found for the current filters, but you can add a new element.`;
  public dataNotFound = $localize`:@@tables.data_not_found:Data wasn't found for the current filters.`;

  private currentPage: number = 1;
  private subscribers: any = {};

  public searchValue = '';
  private rowsBackup: IRow[];

  constructor(
    @Inject(ITABLE_SERVICE) private tableService: ITableService,
    @Inject(StorageMap) public localStorage: StorageMap,
    @Inject('IUnsecuredService') public unsecuredService: IUnsecuredService,
    private filterControlsFactory: FilterControlsFactory,
    private route: ActivatedRoute,
    private router: Router,
    @Inject(TIMEZONE_CONFIG) private timeZoneConfig: TimeZoneConfig
  ) {}

  @HostListener('window:resize')
  onResize() {
    this.updateContainerHeight();
  }

  ngAfterViewInit() {
    this.subscribers.unsecured = this.unsecuredService.getUnsecuredStatus().subscribe(() => {
      this.updateContainerHeight();
    });
  }

  private updateContainerHeight(): void {
    if (this.currentSettings?.type === 'widget') {
      return;
    }
    const container = this.mainContainer?.nativeElement;
    if (container) {
      const offsetTop = container.getBoundingClientRect().top;
      const availableHeight = window.innerHeight - offsetTop - 30;

      container.style.height = `${availableHeight}px`;
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('settings' in changes) {
      this.init();
    }
  }

  private init(): void {
    this.data = null;
    this.filtersModel = [];
    this.visibleColumns = [];
    this.columns = [];
    this.currentSettings = void 0;
    this.displayFilters = [];
    this.routeParams = {};
    this.tableParams = null;
    this.isLoading = true;
    this.routeParams = this.route.snapshot.queryParams;
    this.localStorage.get(this.key + '_selected_key').subscribe((key) => {
      if (Array.isArray(this.settings)) {
        if (this.routeParams?.['tableKey'] || !key) {
          this.currentSettings = this.getTable(this.routeParams?.['tableKey'], this.routeParams, this.settings);
        } else {
          this.currentSettings = this.settings.find((setting) => setting.uniqueKey === key);
        }
      } else {
        this.currentSettings = this.settings;
      }
      if (this.currentSettings?.type !== 'widget') {
        let filterValues: any;
        let params: any;
        for (const [key, value] of Object.entries(this.routeParams)) {
          switch (key) {
            case 'page':
            case 'limit': {
              params = params || {};
              params[key] = value;
              break;
            }
            default: {
              filterValues = filterValues || {};

              if (key.includes('__')) {
                const keys = key.split('__');
                filterValues[keys[0]] = filterValues[keys[0]] || [{}];
                filterValues[keys[0]][0][keys[1]] = value.includes(',') ? value.split(',') : [value];
              } else {
                filterValues[key] = value.split(',');
              }
              break;
            }
          }
        }
        if (filterValues && Object.keys(filterValues).length) {
          this.localStorage.get(this.currentSettings.key + (this.currentSettings.uniqueKey ?? '')).subscribe((filters: any) => {
            filters = filters || {};
            filters.filterValues = filterValues;
            this.localStorage.set(this.currentSettings.key + (this.currentSettings.uniqueKey ?? ''), filters).subscribe();
          });
        }
        if (filterValues || params) {
          this.getData(
            {
              ...params,
              filterValues,
            },
            true
          );
          return;
        }
      }
      if (this.currentSettings?.defaultFilters) {
        this.getData({
          filterValues: this.currentSettings?.defaultFilters,
        });
        return;
      }
      this.getData();
    });
  }

  public getData(
    params?: {
      page?: number;
      limit?: number;
      filterValues?: IFilterValues;
      orderValues?: IOrderValues;
      sortedColumns?: ISortedColumns;
      visibilityValues?: IVisibilityValues;
      scopeValues?: IScopeValues;
    },
    force = false
  ): void {
    this.isLoading = true;
    this.data = null;
    this.visibleColumns = [];
    const tableKey = this.currentSettings?.key;
    if (!tableKey) {
      return;
    }
    this.subscribers.getData?.unsubscribe();
    this.subscribers.getData = this.localStorage
      .get(this.currentSettings.key + (this.currentSettings.uniqueKey ?? ''))
      .pipe(
        map((localStorage: any) => {
          if (this.currentSettings?.type === 'widget') {
            return {};
          }
          if (localStorage?.page) {
            delete localStorage?.page;
          }
          return localStorage || {};
        }),
        switchMap((localStorage: any) => {
          this.resetCheckboxGroup();
          const param = (this.tableParams = {
            page: 1,
            limit: this.currentSettings?.limit || (this.currentSettings?.type === 'widget' ? 10 : 25),
            sortedColumns: params?.sortedColumns || this.currentSettings?.sortedColumns,
            visibilityValues: params?.visibilityValues || this.currentSettings?.visibilityValues,
            orderValues: params?.orderValues || this.tableParams?.orderValues,
            scopeValues: params?.scopeValues || this.currentSettings?.scopeValues,
            ...params,
            ...localStorage,
          });
          if (force) {
            Object.assign(param, params);
          }
          return this.tableService.getData(
            tableKey,
            +param.page,
            +param.limit,
            param.filterValues || {},
            param.orderValues || {},
            param.scopeValues || {}
          );
        }),
        map((data: ITableDataResponse) => {
          this.workaroundForUuidsInsteadOfIds(data);
          const total = !isNaN(data.total) ? Number(data.total) : Infinity;
          const sortedFilters = this.sortingFilters(data.filters, data.columns);
          this.translateFilterLabel(data.filters);
          this.translateColumnLabel(data.columns);
          return {
            ...data,
            filters: sortedFilters,
            filterValues: this.tableParams?.filterValues,
            total: Math.ceil(total),
          };
        }),
        map((data) => {
          if (this.currentSettings?.transformTable) {
            return this.currentSettings.transformTable(data);
          }
          return data;
        })
      )
      .subscribe({
        next: (data) => {
          if (this.tableParams) {
            this.tableParams.limit = data.limit;
            this.tableParams.page = data.page;
          }

          const total = +(data?.total || 0);
          const limit = this.tableParams?.limit || 25;
          const page = this.tableParams?.page || 1;
          if (total > 0 && Math.ceil(total / limit) < page) {
            this.getData(
              {
                ...params,
                page: 1,
              },
              force
            );
            return;
          }

          data.columns = this.currentSettings.columnsIdsToHide
            ? data.columns.filter((column) => !this.currentSettings.columnsIdsToHide.includes(column.id))
            : data.columns;

          this.columns = JSON.parse(JSON.stringify(data.columns));
          if (this.currentSettings?.checkboxesConfig?.formGroup) {
            this.setControl(data.rows);
          }

          this.pagination = {
            total,
            page,
            limit,
          };
          this.data = data;
          this.rowsBackup = data.rows;
          this.updateFilters();
          if (this.currentSettings?.type !== 'widget') {
            this.setRouteParams({ ...data.filterValues, limit: data.limit, page: data.page }, false);
          }
          this.changeTableSettings(false, clearObj(this.tableParams || {})).subscribe(() => {
            this.isLoading = false;
            this.updateContainerHeight();
          });
        },
        error: () => {
          this.resetFilters().subscribe();
        },
      });
  }

  public changeTable(table: TableInterface): void {
    this.currentSettings = table;
    if (table.uniqueKey) {
      this.localStorage.set(this.key + '_selected_key', table.uniqueKey).subscribe();
    }
    const params: Record<string, any> = {
      filterValues: table.defaultFilters,
    };
    if (table.limit) {
      params.limit = table.limit;
    }
    this.getData(params, true);
  }

  private sortingFilters(filters: IFilter[], columns: IColumn[]): IFilter[] {
    return [...filters].sort(({ id: aID }, { id: bID }) => {
      const aIndex = columns.findIndex((column) => column.id === aID);
      const bIndex = columns.findIndex((column) => column.id === bID);
      if (aIndex === -1) {
        return 1;
      } else if (bIndex === -1) {
        return -1;
      }
      return aIndex - bIndex;
    });
  }

  private translateFilterLabel(filters: IFilter[]): void {
    filters.forEach((filter) => {
      filter.label = TABLE_TRANSLATIONS[filter.label] || filter.label;
      filter.group = TABLE_TRANSLATIONS[filter.group] || filter.group;
    });
  }

  private translateColumnLabel(columns: IColumn[]): void {
    return columns.forEach((col) => {
      col.label = TABLE_TRANSLATIONS[col.label] || col.label;
    });
  }

  private setRouteParams(params: any, merge = false): void {
    const queryParams: Record<string, string> = {};
    for (const paramKey in params) {
      if (Array.isArray(params[paramKey]) && typeof params[paramKey][0] === 'object') {
        Object.keys(params[paramKey][0]).forEach((key) => {
          const value = params[paramKey][0][key]?.toString() ?? null;
          if (value) {
            queryParams[`${paramKey}__${key}`] = value;
          }
        });
      } else {
        const item = params[paramKey]?.toString();
        if (item) {
          queryParams[paramKey] = item;
        }
      }
    }
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: merge ? 'merge' : '',
      replaceUrl: true,
    });
  }

  private setControl(rows: IRow[]): void {
    for (const { id } of rows) {
      if (!this.currentSettings?.checkboxesConfig?.formGroup?.get([id])) {
        this.currentSettings?.checkboxesConfig?.formGroup?.addControl(id, new UntypedFormControl());
      }
    }
  }

  private updateFilters(): void {
    this.displayFilters = [];
    this.quickFilters = this.data?.filters
      .filter((filter) => filter.quickSearch)
      .map((item) => ({
        ...item,
        isQuickFilter: true,
      }));
    if (this.quickFilters && this.data) {
      this.filtersModel = this.data.filters.concat(this.quickFilters).map((field) => {
        const { group, label } = field;
        const modelValidation = this.filtersModel?.find((item) => item.model.getComponent().settings.key === field.id);
        const control = modelValidation?.model.getComponent().settings.control ?? new UntypedFormControl();
        const model = this.filterControlsFactory.getField(field.type, field.id, field.label, control);
        const value = this.data?.filterValues?.[field.id] ?? null;
        control.setValue(value, { emitEvent: false, onlySelf: true });
        if (!this.displayFilters.find((item) => item.key === field.id) && control.value !== null) {
          this.displayFilters.push(getDisplayFilters(model, this.timeZoneConfig));
        }
        const isQuickFilter = !!field.isQuickFilter;
        return {
          group,
          model,
          label,
          isQuickFilter,
        };
      });
    }
  }

  public get tables(): TableInterface[] {
    if (Array.isArray(this.settings)) {
      return this.settings;
    }
    return [];
  }

  private resetCheckboxGroup(): void {
    if (this.currentSettings?.checkboxesConfig?.formGroup) {
      this.currentSettings.checkboxesConfig.formGroup.reset();
    }
    if (this.table) {
      this.table.isIndeterminate = false;
      this.table.isAllChecked = false;
    }
  }

  public changeFilters(filterValues: IFilterValues = {}): void {
    this.subscribers['resetFilters'] = this.resetFilters()
      .pipe(
        switchMap(() =>
          this.changeTableSettings(true, {
            filterValues,
          })
        )
      )
      .subscribe((settings) => {
        this.resetCheckboxGroup();
        this.getData(settings);
      });
  }

  public sort(orderValues: IOrderValues): void {
    this.changeTableSettings(false).subscribe((settings) => {
      settings.filterValues = settings.filterValues || this.currentSettings?.defaultFilters;
      if (this.currentSettings?.limit) {
        settings.limit = this.currentSettings?.limit;
      }
      this.getData({ ...settings, orderValues, page: 1 });
    });
  }

  public refresh(): void {
    this.changeTableSettings(false).subscribe((settings) => {
      settings.filterValues = settings.filterValues || this.currentSettings?.defaultFilters;
      if (this.currentSettings?.limit) {
        settings.limit = this.currentSettings?.limit;
      }
      this.getData({
        ...settings,
        page: 1,
      });
    });
  }

  public update(): void {
    this.changeTableSettings(false).subscribe((settings) => {
      settings.filterValues = settings.filterValues || this.currentSettings?.defaultFilters;
      if (this.currentSettings?.limit) {
        settings.limit = this.currentSettings?.limit;
      }
      this.getData({ ...settings, page: this.currentPage });
    });
  }

  public changePage(page: number): void {
    this.currentPage = page;

    this.changeTableSettings(false).subscribe((settings) => {
      settings.filterValues = settings.filterValues || this.currentSettings?.defaultFilters;
      if (this.currentSettings?.limit) {
        settings.limit = this.currentSettings?.limit;
      }
      this.getData({ ...settings, page });
    });
  }

  public changeLimit(limit?: number): void {
    if (limit && limit !== this.tableParams?.limit) {
      this.changeTableSettings(true, { limit }).subscribe((settings) => {
        settings.filterValues = settings.filterValues || this.currentSettings?.defaultFilters;
        this.getData({ ...settings, page: 1, limit });
      });
    }
  }

  private resetFilters(): Observable<any> {
    return this.localStorage.get(this.currentSettings.key + (this.currentSettings.uniqueKey ?? '')).pipe(
      map((filters: any) => {
        if (filters) {
          delete filters.filterValues;
        }
        return filters;
      }),
      switchMap((filters) => this.localStorage.set(this.currentSettings.key + (this.currentSettings.uniqueKey ?? ''), filters))
    );
  }

  private getTable(tableKey: string | undefined, params: Record<string, unknown>, settings: TableInterface[]): TableInterface {
    const table = (this.settings as TableInterface[]).find((table) => table.key === tableKey);
    if (table) {
      return table;
    }
    for (const [key, value] of Object.entries(params)) {
      for (const setting of settings) {
        if (setting.defaultFilters?.[key] === value) {
          return setting;
        }
      }
    }
    return settings[0];
  }

  public changeTableSettings(save: boolean, settings: Partial<ITableSettings> = {}): Observable<ITableSettings> {
    if (this.currentSettings?.type === 'widget') {
      if (settings.filterValues) {
        Object.assign(settings.filterValues, this.currentSettings?.defaultFilters ?? {});
      } else {
        settings.filterValues = this.currentSettings?.defaultFilters;
      }
    }
    return this.localStorage.get(this.currentSettings.key + (this.currentSettings.uniqueKey ?? '')).pipe(
      map((data: any) => {
        if (settings.sortedColumns?.length === 0) {
          delete data?.sortedColumns;
          delete data?.visibilityValues;
          delete settings.sortedColumns;
          delete settings.visibilityValues;
        }
        if (settings.visibilityValues) {
          settings.visibilityValues = clearObj(settings.visibilityValues);
        }
        const visibilityValues = data?.visibilityValues || settings.visibilityValues;
        if (visibilityValues && !Object.keys(visibilityValues).length) {
          delete data?.visibilityValues;
          delete settings.visibilityValues;
        }
        if (settings.filterValues) {
          const entries = Object.entries(settings.filterValues);
          if (entries.length) {
            const mappedFilters: IFilterValues = {};
            for (const [key, value] of entries) {
              switch (true) {
                case Array.isArray(value) && !value.length:
                case value === null:
                case value === '':
                  break;
                default:
                  mappedFilters[key] = Array.isArray(value) ? value : [value];
                  break;
              }
            }
            settings.filterValues = mappedFilters;
          } else {
            delete data?.filterValues;
            delete settings.filterValues;
            if (this.currentSettings?.defaultFilters) {
              this.currentSettings.defaultFilters = {};
            }
          }
        }
        return Object.assign({}, data, settings);
      }),
      switchMap((filters: ITableSettings) => {
        if (save) {
          return this.localStorage.set(this.currentSettings.key + (this.currentSettings.uniqueKey ?? ''), filters).pipe(map(() => filters));
        }
        return of(filters);
      }),
      tap(({ visibilityValues, sortedColumns }) => {
        sortedColumns = sortedColumns || this.currentSettings?.sortedColumns || this.data?.columns?.map((item) => item.id);
        if (sortedColumns) {
          const indexMap = new Map(sortedColumns.map((item, i) => [item, i]));
          for (let i = 0; i < this.columns.length; i++) {
            const column = this.columns[i];
            if (!indexMap.has(column.id)) {
              indexMap.set(column.id, i);
            }
          }
          this.columns = this.columns.sort((a, b) => {
            const aIdx = indexMap.get(a.id);
            const bIdx = indexMap.get(b.id);
            if (typeof aIdx === 'number' && typeof bIdx == 'number') {
              return aIdx - bIdx;
            }
            return 0;
          });
        }
        visibilityValues =
          visibilityValues ||
          this.currentSettings?.visibilityValues ||
          this.columns.reduce<IVisibilityValues>((acc, item) => {
            if (item.visible_by_default) {
              acc[item.id] = true;
            }
            return acc;
          }, {});
        if (this.tableParams) {
          this.tableParams.visibilityValues = visibilityValues;
        }
        this.visibleColumns = this.columns
          .filter((column) => visibilityValues?.[column.id])
          .map((column) =>
            column.type === 'type_protected'
              ? {
                  ...column,
                  isUnsecured: this.unsecuredService.isUnsecuredValue,
                }
              : column
          );
      })
    );
  }

  public onSearchChange(input: string): void {
    this.data.rows = this.rowsBackup.filter((row) => row[this.currentSettings.searchKey].toLowerCase().includes(input.toLowerCase()));
  }

  private workaroundForUuidsInsteadOfIds(data: ITableDataResponse): void {
    if (this.key === 'failedJobsTable') {
      data.rows.forEach((row: IRow) => {
        if (row.uuid) {
          row.id = row.uuid;
          delete row.uuid;
        }
      });
      data.columns.forEach((column: IColumn) => {
        if (column.id === 'uuid') {
          column.id = 'id';
        }
      });
    }
  }
}
